import {Store} from '@/plugins/Store';
import {Environment} from '@/utils/Environment';
import type {Article, ArticleContentKey} from '@/model/Article';

/** How long local Preview waits for Lucy before showing empty editors. */
export const LOCAL_AI_WAIT_MS = 16_000;

export function isLocalPreviewAi(): boolean {
    return Environment.isLocalPreviewHost();
}

export function delay(ms: number): Promise<'timeout'> {
    return new Promise(resolve => {
        window.setTimeout(() => resolve('timeout'), ms);
    });
}

export function raceWithLocalAiTimeout<T>(work: Promise<T>): Promise<T | 'timeout'> {
    if (!isLocalPreviewAi()) {
        return work;
    }
    return Promise.race([work, delay(LOCAL_AI_WAIT_MS)]);
}

export function clearArticleAiLoadingFlags(): void {
    const article: Article = Store.getters.article;
    if (!article) {
        return;
    }
    const loading = Store.getters.articleAiLoading;
    Store.commit('setArticleMetaAiLoading', {metaPart: 'metaTitle', value: false});
    Store.commit('setArticleMetaAiLoading', {metaPart: 'metaDescription', value: false});
    for (let level = 1; level <= 6; level++) {
        const part = ('h' + level) as ArticleContentKey;
        const blocks = (article as any)[part] || [];
        const loaders = (loading && (loading as any)[part]) || [];
        const max = Math.max(blocks.length, loaders.length);
        for (let index = 0; index < max; index++) {
            Store.commit('setArticleContentAiLoading', {
                contentPart: part,
                index,
                contentKey: 'h',
                value: false
            });
            Store.commit('setArticleContentAiLoading', {
                contentPart: part,
                index,
                contentKey: 'p',
                value: false
            });
        }
    }
    const questions = article.questions || [];
    const questionLoaders = (loading && loading.questions) || [];
    const questionMax = Math.max(questions.length, questionLoaders.length);
    for (let index = 0; index < questionMax; index++) {
        Store.commit('setArticleQuestionAiLoading', {
            index,
            questionKey: 'question',
            value: false
        });
        Store.commit('setArticleQuestionAiLoading', {
            index,
            questionKey: 'answer',
            value: false
        });
    }
    Store.commit('trimArticleAiLoading');
    Store.commit('setArticleSeoScoreAiLoadingCompleted', true);
    Store.commit('setArticleSeoScoreChecked', true);
    Store.commit('setArticleAiPolishing', false);
    Store.commit('setArticleAiEnhancing', false);
}

/** Empty H1–H3 blocks so the editor is usable without generated copy. */
export function ensureArticleEditorSkeleton(): void {
    const article: Article = Store.getters.article;
    if (!article) {
        return;
    }
    (['h1', 'h2', 'h3'] as ArticleContentKey[]).forEach((part) => {
        const blocks = (article as any)[part] || [];
        if (!blocks.length) {
            Store.commit('insertArticleContentSubPart', {
                contentPart: part,
                index: 0,
                aiLoading: false,
                keepContent: false
            });
        }
    });
}

export function unlockLocalArticleEditors(): void {
    ensureArticleEditorSkeleton();
    clearArticleAiLoadingFlags();
}
